/**
 * Справочники для форм выбора: подразделения и сотрудники.
 *
 * Отдельно от администрирования намеренно: создавать доску и приглашать
 * участников может не только администратор. Прежде формы держали эти
 * данные константами с выдуманными идентификаторами («dep-oa», «prj-uor»),
 * и через интерфейс нельзя было создать ни доску, ни приглашение —
 * сервер не принимал такие идентификаторы.
 */

import { api } from "../api";

export interface Department {
  id: string;
  /** Неизменяемый ключ: стоит в адресах (?department=OA). Базовые — OA,
   *  OOA, OAP (DepartmentCode), остальные заводит администратор. */
  code: string;
  /** Подпись вкладки и списков. */
  name: string;
  /** Вышестоящее подразделение; null — верхний уровень. */
  parentId?: string | null;
  /** Порядок среди соседей по дереву. */
  position?: number;
  /** Базовое (ОА, ООА, ОАП) — не удаляется. */
  isBase?: boolean;
  boardCount?: number;
  userCount?: number;
  /** Норма нагрузки, баллов на сотрудника; null — не задана. */
  workloadNorm?: number | null;
}

/** Подразделение в дереве: глубина — для отступа в списках. */
export interface DepartmentNode extends Department {
  depth: number;
}

/**
 * Подразделения обходом дерева: родитель, за ним его подчинённые.
 * Среди соседей — по position, затем по названию. Подразделение,
 * чей родитель не пришёл (удалён или скрыт), встаёт на верхний уровень.
 */
export function departmentTree(items: readonly Department[]): DepartmentNode[] {
  const known = new Set(items.map((item) => item.id));
  const children = new Map<string | null, Department[]>();
  for (const item of items) {
    const parent = item.parentId && known.has(item.parentId) ? item.parentId : null;
    const list = children.get(parent) ?? [];
    list.push(item);
    children.set(parent, list);
  }
  const order = (a: Department, b: Department) =>
    (a.position ?? 0) - (b.position ?? 0) || a.name.localeCompare(b.name, "ru");
  const result: DepartmentNode[] = [];
  const seen = new Set<string>();
  const walk = (parent: string | null, depth: number) => {
    for (const item of [...(children.get(parent) ?? [])].sort(order)) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      result.push({ ...item, depth });
      walk(item.id, depth + 1);
    }
  };
  walk(null, 0);
  return result;
}

/** Подпись в выпадающем списке: отступ показывает подчинённость. */
export function departmentOptionLabel(item: DepartmentNode): string {
  return `${"\u2003".repeat(item.depth)}${item.name}`;
}

/** Все подчинённые подразделения (вся ветка) — их нельзя выбрать вышестоящим. */
export function departmentDescendants(items: readonly Department[], id: string): Set<string> {
  const result = new Set<string>();
  let frontier = [id];
  while (frontier.length > 0) {
    const next = items.filter((item) => item.parentId && frontier.includes(item.parentId));
    frontier = next.map((item) => item.id).filter((item) => !result.has(item));
    for (const item of frontier) result.add(item);
  }
  return result;
}

/** Строка справочника сотрудников. Почты нет намеренно: для выбора она не нужна. */
export interface DirectoryUser {
  id: string;
  firstName: string;
  lastName: string;
  middleName: string | null;
  position: string | null;
  departmentName: string;
  avatarUrl: string | null;
}

export const directoryApi = api.injectEndpoints({
  endpoints: (build) => ({
    departments: build.query<{ data: Department[] }, void>({
      query: () => ({ method: "departments.list" }),
      providesTags: ["Department"],
    }),

    userDirectory: build.query<{ data: DirectoryUser[] }, { search?: string }>({
      query: (params) => ({ method: "users.directory", params }),
      providesTags: ["UserList"],
    }),
  }),
});

export const { useDepartmentsQuery, useUserDirectoryQuery } = directoryApi;
